/**
 * Tool life monitoring – single source of truth for all calculations.
 * Used by ToolInspectionPage, MachineToolCard, ShiftLogDetailDialog.
 *
 * Definitions:
 * - Checkpoint QTY is cumulative (running total). Each row's qty must be > previous row (and non-negative).
 * - Phase life (between two consecutive checkpoints): qty[i] - qty[i-1] (pieces run in that phase). Computed with abs for safety.
 * - Total inserts used: count of checkpoints where insert_phase === 1 (N = new insert). If no N is selected, inserts used is 0.
 * - Average phase life: mean of all consecutive phase-life values (null if fewer than 2 checkpoints).
 *
 * Future: target validation – e.g. last cumulative qty vs total target, or sum of phase lives vs target.
 * When target is auto-calculated, validateTotalTarget(checkpoints, target) can be used here.
 */

export function computePhaseLife(checkpoints, index) {
  if (!checkpoints.length || index <= 0 || index >= checkpoints.length) return null;
  return Math.abs((checkpoints[index].qty ?? 0) - (checkpoints[index - 1].qty ?? 0));
}

function isNewInsert(cp) {
  return cp.insert_phase === 1;
}

export function computeToolStats(checkpoints) {
  if (!checkpoints || !checkpoints.length) {
    return { totalInserts: 0, avgLife: null };
  }
  let totalInserts = 0;
  const lives = [];
  for (let i = 0; i < checkpoints.length; i++) {
    if (isNewInsert(checkpoints[i])) totalInserts++;
    if (i > 0) lives.push(Math.abs((checkpoints[i].qty ?? 0) - (checkpoints[i - 1].qty ?? 0)));
  }
  const avgLife = lives.length > 0 ? lives.reduce((a, b) => a + b, 0) / lives.length : null;
  return { totalInserts, avgLife };
}

/**
 * Compare actual phase life against a standard range { min, max }.
 * Returns null if inputs are missing, otherwise { value, status }.
 * - status 'below': life < min (value is negative delta from min)
 * - status 'above': life > max (value is positive delta from max)
 * - status 'ok': within range (value is 0)
 */
export function computeDeviation(life, standardRange) {
  if (!standardRange || life == null) return null;
  if (life < standardRange.min) return { value: life - standardRange.min, status: 'below' };
  if (life > standardRange.max) return { value: life - standardRange.max, status: 'above' };
  return { value: 0, status: 'ok' };
}

/**
 * Future: validate that checkpoints satisfy target (e.g. last cumulative qty === target, or sum of phase lives === target).
 * Placeholder for when total target is auto-calculated and we enforce all checkpoints + ... = target.
 */
export function validateTotalTarget(_checkpoints, _target) {
  return { valid: true };
}
